/**
 * Registration Field Validation
 * Real-time AJAX validation for registration fields
 */

class RegistrationValidator {
    constructor(config = {}) {
        this.config = {
            endpoint: '/index.php?aio=checkRegistrationField',
            minLength: 3,
            debounceTime: 500,
            debug: false,
            ...config
        };

        this.debounceTimers = {};
        this.init();
    }

    /**
     * Initialize validation
     */
    init() {
        this._log('Initializing registration field validation');
        this.setupFieldValidation();
    }

    /**
     * Setup validation for all fields
     */
    setupFieldValidation() {
        // Username validation
        this.attachValidation('reg-username', 'regUsername', 'login');

        // Email validation
        this.attachValidation('reg-email', 'regEmail', 'email');
    }

    /**
     * Attach validation to a field
     * @param {string} inputId - DOM element ID
     * @param {string} backendField - Backend field name
     * @param {string} fieldType - Field type for display
     */
    attachValidation(inputId, backendField, fieldType) {
        const input = document.getElementById(inputId);
        if (!input) {
            this._log(`Field ${inputId} not found`);
            return;
        }

        input.addEventListener('blur', () => {
            this.validateField(inputId, backendField, fieldType);
        });

        input.addEventListener('input', () => {
            // Debounced validation on input
            clearTimeout(this.debounceTimers[inputId]);
            this.debounceTimers[inputId] = setTimeout(() => {
                this.validateField(inputId, backendField, fieldType);
            }, this.config.debounceTime);
        });

        this._log(`Attached validation to ${inputId}`);
    }

    /**
     * Validate field via AJAX
     * @param {string} inputId - DOM element ID
     * @param {string} backendField - Backend field name
     * @param {string} fieldType - Field type for display
     */
    async validateField(inputId, backendField, fieldType) {
        const input = document.getElementById(inputId);
        const value = input.value.trim();

        // Clear previous state
        this.clearFieldState(input);

        // Skip validation for empty or too short values
        if (value.length < this.config.minLength) {
            return;
        }

        // Show loading state
        this.setLoadingState(input, true);

        try {
            const formData = new FormData();
            formData.append('field', backendField);
            formData.append('value', value);

            const response = await fetch(this.config.endpoint, {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: formData
            });

            let data;
            try {
                data = await response.json();
            } catch (e) {
                throw new Error('Invalid JSON response from server');
            }

            this._log(`Validation response for ${fieldType}:`, data);

            // Handle response
            if (data.result === 1 && data.available === true) {
                // Field is available
                this.setFieldSuccess(input, `${this.getFieldLabel(fieldType)} je dostupné`);
            } else {
                // Field is NOT available or error
                // Backend může vracet 'message' nebo 'error_message'
                const message = data.message || data.error_message || `${this.getFieldLabel(fieldType)} již existuje`;
                this.setFieldError(input, message);
            }
        } catch (error) {
            this._log('Validation error:', error);

            if (error.message.includes('429') || error.message.includes('503')) {
                this.setFieldError(input, 'Příliš mnoho pokusů, zkuste později');
            } else {
                // On error, don't show anything - let HTML5 validation handle it
                this.clearFieldState(input);
            }
        } finally {
            this.setLoadingState(input, false);
        }
    }

    /**
     * Get user-friendly field label
     */
    getFieldLabel(fieldType) {
        const labels = {
            login: 'Přihlašovací jméno',
            email: 'E-mail',
            ico: 'IČO'
        };
        return labels[fieldType] || 'Pole';
    }

    /**
     * Set loading state
     */
    setLoadingState(input, loading) {
        if (loading) {
            input.classList.add('validating');
        } else {
            input.classList.remove('validating');
        }
    }

    /**
     * Set field success state
     */
    setFieldSuccess(input, message) {
        input.classList.remove('is-invalid');
        input.classList.add('is-valid');

        // Don't show success message - green border is enough visual feedback
        const feedback = this.getFeedbackElement(input, 'valid');
        if (feedback) {
            feedback.textContent = '';
        }
    }

    /**
     * Set field error state
     */
    setFieldError(input, message) {
        input.classList.remove('is-valid');
        input.classList.add('is-invalid');

        const feedback = this.getFeedbackElement(input, 'invalid');
        if (feedback) {
            feedback.textContent = message;
        }
    }

    /**
     * Clear field state
     */
    clearFieldState(input) {
        input.classList.remove('is-valid', 'is-invalid', 'validating');
    }

    /**
     * Get or create feedback element
     */
    getFeedbackElement(input, type) {
        const className = type === 'valid' ? 'valid-feedback' : 'invalid-feedback';
        let feedback = input.parentElement.querySelector(`.${className}`);

        if (!feedback) {
            feedback = document.createElement('div');
            feedback.className = className;
            input.parentElement.appendChild(feedback);
        }

        return feedback;
    }

    /**
     * Debug logging
     */
    _log(...args) {
        if (this.config.debug) {
            console.log('[RegistrationValidator]', ...args);
        }
    }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        // Check if we have auth config to get debug setting
        const debug = window.redaAuth?.config?.debug || false;

        window.registrationValidator = new RegistrationValidator({
            debug: debug
        });
    });
} else {
    const debug = window.redaAuth?.config?.debug || false;
    window.registrationValidator = new RegistrationValidator({
        debug: debug
    });
}

export default RegistrationValidator;
